import React, { useState, useEffect } from 'react';
import { 
  Users, 
  Plus, 
  Trash2, 
  Edit3, 
  Save, 
  Clock, 
  Upload, 
  Image as ImageIcon, 
  Eye, 
  X 
} from 'lucide-react';
import { useToast } from '../../../components/Toast';
import { getImageUrl as resolveMediaSrc } from '../../../utils/mediaUtils';
import './FormsUnified.css';

const MAX_IMAGE_SIZE = 5 * 1024 * 1024;
const ALLOWED_TYPES = ['image/jpeg', 'image/png', 'image/webp'];

export default function AboutUsLeadershipForm({ apiBase }) {
  const { toast, ToastContainer } = useToast();

  const [formData, setFormData] = useState({
    kicker: 'School Leadership',
    title: 'Profiles of the Principal & Key Staff',
    subtitle: 'Meet the leaders who shape our culture, strengthen academics, and support every student journey.',
    items: []
  });
  const [originalData, setOriginalData] = useState({
    kicker: 'School Leadership',
    title: 'Profiles of the Principal & Key Staff',
    subtitle: 'Meet the leaders who shape our culture, strengthen academics, and support every student journey.',
    items: []
  });
  const [loading, setLoading] = useState(true);
  const [sectionId, setSectionId] = useState(null);
  const [submitting, setSubmitting] = useState({});
  const [pendingItems, setPendingItems] = useState({});
  const [pendingHeader, setPendingHeader] = useState({});
  const [imageUploading, setImageUploading] = useState(false);
  const [imageProgress, setImageProgress] = useState(0);
  const [mediaMap, setMediaMap] = useState({});
  const [editingIndex, setEditingIndex] = useState(null);
  const [modalData, setModalData] = useState(null);
  const [lightboxImage, setLightboxImage] = useState(null);

  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apiBase]);

  const loadMediaMap = async () => {
    try {
      const response = await fetch(
        `${apiBase}/aboutus/cms/media?_t=${Date.now()}`,
        { 
          credentials: 'include',
          headers: { 'Cache-Control': 'no-cache', 'Pragma': 'no-cache' }
        }
      );
      
      if (!response.ok) return;
      
      const result = await response.json();
      if (result.ok && Array.isArray(result.data)) {
        const map = {};
        result.data.forEach((media) => {
          const mediaId = media.id || media.media_id;
          const mediaUrl = media.url || media.file_path;
          if (mediaId && mediaUrl) {
            map[mediaId] = mediaUrl;
            map[String(mediaId)] = mediaUrl;
          }
        });
        setMediaMap(map);
      }
    } catch (err) {
      console.error('[AboutUsLeadershipForm] Media fetch error:', err.message);
    }
  };

  const getMediaUrl = (mediaId) => {
    if (!mediaId) return null;
    if (mediaMap[mediaId]) return mediaMap[mediaId];
    if (mediaMap[String(mediaId)]) return mediaMap[String(mediaId)];
    return resolveMediaSrc(mediaId, mediaMap) || mediaId;
  };

  const loadData = async () => {
    await Promise.all([loadMediaMap(), loadSection()]);
  };

  const loadSection = async () => {
    setLoading(true);
    try {
      let leadershipSection = null;
      
      const adminResponse = await fetch(
        `${apiBase}/aboutus/cms/sections`,
        { 
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' }
        }
      );
      
      if (adminResponse.ok) {
        const result = await adminResponse.json();
        if (result.ok && Array.isArray(result.data)) {
          leadershipSection = result.data.find(s => s.section_type === 'about-us-leadership-shell');
        }
      }
      
      if (!leadershipSection) {
        const publicResponse = await fetch(
          `${apiBase}/aboutus/cms/sections?published=true`,
          { 
            credentials: 'include',
            headers: { 'Content-Type': 'application/json' }
          }
        );
        
        if (publicResponse.ok) {
          const result = await publicResponse.json();
          if (result.ok && Array.isArray(result.data)) {
            leadershipSection = result.data.find(s => s.section_type === 'about-us-leadership-shell');
          }
        }
      }
      
      if (!leadershipSection) {
        throw new Error('Leadership section not found');
      }

      const currentSectionId = leadershipSection.id;
      setSectionId(currentSectionId);

      let published = leadershipSection.content_published;
      if (typeof published === 'string') {
        try { published = JSON.parse(published); } catch (e) { published = {}; }
      }
      const en = published?.en || {};

      const initialKicker = en.kicker || 'School Leadership';
      const initialTitle = en.title || 'Profiles of the Principal & Key Staff';
      const initialSubtitle = en.subtitle || en.intro || 'Meet the leaders who shape our culture, strengthen academics, and support every student journey.';
      const initialItems = Array.isArray(en.items) ? [...en.items] : [];

      const initialData = {
        kicker: initialKicker,
        title: initialTitle,
        subtitle: initialSubtitle,
        items: initialItems
      };

      const pendingMap = {};
      const pendingHeaderMap = {};

      try {
        const pendingRes = await fetch(`${apiBase}/cms/section/${currentSectionId}/with-pending`, {
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' }
        });
        if (pendingRes.ok) {
          const pendingData = await pendingRes.json();
          if (pendingData.success && Array.isArray(pendingData.data?.pending_changes)) {
            pendingData.data.pending_changes.forEach(ch => {
              if (ch.status === 'pending' && (ch.locale === 'en' || !ch.locale)) {
                let val = ch.new_value;
                if (typeof val === 'string') {
                  try { val = JSON.parse(val); } catch (e) {}
                }

                const match = ch.field_id.match(/^items\.(\d+)(?:\.|$)/);
                if (match) {
                  const idx = parseInt(match[1], 10);
                  pendingMap[idx] = true;
                  if (val && initialData.items[idx]) {
                    if (typeof val === 'object' && val !== null) {
                      delete val.update;
                    }
                    initialData.items[idx] = {
                      ...initialData.items[idx],
                      ...val
                    };
                  }
                } else if (ch.field_id === 'kicker' || ch.field_id === 'title' || ch.field_id === 'subtitle') {
                  pendingHeaderMap[ch.field_id] = true;
                  initialData[ch.field_id] = val;
                }
              }
            });
          }
        }
      } catch (pErr) {
        console.warn('[AboutUsLeadershipForm] Error loading pending changes:', pErr);
      }

      setFormData(initialData);
      setOriginalData(initialData);
      setPendingItems(pendingMap);
      setPendingHeader(pendingHeaderMap);
    } catch (err) {
      console.error('[AboutUsLeadershipForm] Load error:', err);
      toast.error('Error loading leadership team: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleFieldChange = (fieldName, value) => {
    setFormData(prev => ({
      ...prev,
      [fieldName]: value
    }));
  };

  const handleSubmitHeaderField = async (fieldName, label) => {
    if (!sectionId) {
      toast.error('Leadership section ID not loaded');
      return;
    }

    if (JSON.stringify(formData[fieldName]) === JSON.stringify(originalData[fieldName])) {
      toast.warning('No changes to submit');
      return;
    }

    setSubmitting(prev => ({ ...prev, [fieldName]: true }));

    try {
      const savedToken = localStorage.getItem('ph_auth_token') || localStorage.getItem('auth_token') || sessionStorage.getItem('token');
      const headers = { 'Content-Type': 'application/json' };
      if (savedToken) {
        headers['Authorization'] = `Bearer ${savedToken}`;
      }

      const response = await fetch(`${apiBase}/cms/field-change/submit`, {
        method: 'POST',
        headers,
        credentials: 'include',
        body: JSON.stringify({
          section_id: sectionId,
          field_id: fieldName,
          field_name: label || fieldName.replace(/_/g, ' '),
          new_value: formData[fieldName],
          old_value: originalData[fieldName],
          locale: 'en'
        })
      });

      const result = await response.json();
      if (result.success || response.ok) {
        toast.success(`${label || fieldName} submitted for approval`);
        setPendingHeader(prev => ({ ...prev, [fieldName]: true }));
        setOriginalData(prev => ({ ...prev, [fieldName]: formData[fieldName] }));
      } else {
        if (response.status === 409) {
          toast.error('This field already has a pending approval');
        } else {
          toast.error(result.error || result.message || 'Submit failed');
        }
      }
    } catch (err) {
      toast.error('Error: ' + err.message);
    } finally {
      setSubmitting(prev => ({ ...prev, [fieldName]: false }));
    }
  };

  const handleCloseModal = () => {
    if (editingIndex !== null && editingIndex >= originalData.items.length) {
      setFormData(prev => ({
        ...prev,
        items: prev.items.filter((_, i) => i !== editingIndex)
      }));
    }
    setEditingIndex(null);
    setModalData(null);
  };

  const handleAddItem = () => {
    const newItem = { name: '', role: '', bio: '', photo: '' };
    const nextIdx = formData.items.length;
    setFormData(prev => ({
      ...prev,
      items: [...prev.items, newItem]
    }));
    setEditingIndex(nextIdx);
    setModalData(newItem);
  };

  const handleDeleteItem = async (index) => {
    if (pendingItems[index]) {
      toast.info('This profile is awaiting approval');
      return;
    }

    const itemToDelete = formData.items[index];
    if (!itemToDelete) return;

    if (!window.confirm(`Delete profile of ${itemToDelete.name || 'this member'}?`)) return;

    setSubmitting(prev => ({ ...prev, [`del_${index}`]: true }));
    try {
      const savedToken = localStorage.getItem('ph_auth_token') || localStorage.getItem('auth_token') || sessionStorage.getItem('token');
      const headers = { 'Content-Type': 'application/json' };
      if (savedToken) {
        headers['Authorization'] = `Bearer ${savedToken}`;
      }

      const response = await fetch(`${apiBase}/cms/field-change/submit`, {
        method: 'POST',
        headers,
        credentials: 'include',
        body: JSON.stringify({
          section_id: sectionId,
          field_id: `items.${index}.delete`,
          field_name: `Delete Leadership Profile - ${itemToDelete.name || index + 1}`,
          old_value: itemToDelete,
          new_value: null,
          locale: 'en'
        })
      });
      const result = await response.json();
      if (result.success || response.ok) {
        toast.success(`${itemToDelete.name || 'Member'} deletion submitted for approval`);
        setPendingItems(prev => ({ ...prev, [index]: true }));
      } else {
        toast.error(result.error || 'Delete submission failed');
      }
    } catch (err) {
      toast.error('Error: ' + err.message);
    } finally {
      setSubmitting(prev => ({ ...prev, [`del_${index}`]: false }));
    }
  };

  const handleModalDataChange = (field, value) => {
    setModalData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (file.size > MAX_IMAGE_SIZE) {
      toast.error(`Max 5MB, got ${(file.size / 1024 / 1024).toFixed(2)}MB`);
      return;
    }

    if (!ALLOWED_TYPES.includes(file.type)) {
      toast.error('Only JPG, PNG, WebP allowed');
      return;
    }

    setImageUploading(true);
    setImageProgress(0);

    try {
      const formDataToUpload = new FormData();
      formDataToUpload.append('file', file);

      const xhr = new XMLHttpRequest();

      xhr.upload.addEventListener('progress', (ev) => {
        if (ev.lengthComputable) {
          const percent = Math.round((ev.loaded / ev.total) * 100);
          setImageProgress(percent);
        }
      });

      xhr.addEventListener('load', () => {
        if (xhr.status === 200 || xhr.status === 201) {
          try {
            const response = JSON.parse(xhr.responseText);
            if (response.success || response.ok) {
              const mediaId = response.media_id || response.id;
              if (mediaId) {
                handleModalDataChange('photo', mediaId);
                toast.success('Photo uploaded successfully');
              } else {
                toast.error('No media ID returned');
              }
            } else {
              toast.error(response.error || 'Upload failed');
            }
          } catch (parseErr) {
            toast.error('Invalid server response');
          }
        } else {
          toast.error(`Upload failed (HTTP ${xhr.status})`);
        }
        setImageUploading(false);
      });

      xhr.addEventListener('error', () => {
        toast.error('Upload failed - network error');
        setImageUploading(false);
      });

      xhr.open('POST', `${apiBase}/aboutus/cms/media/upload?section_type=about-us-leadership-shell`);
      xhr.withCredentials = true;
      xhr.send(formDataToUpload);
    } catch (err) {
      toast.error('Upload error: ' + err.message);
      setImageUploading(false);
    }
  };

  const handleSubmitModal = async () => {
    if (!sectionId) {
      toast.error('Section ID not loaded');
      return;
    }

    const itemIndex = editingIndex;
    const item = modalData;
    const itemKey = `item_${itemIndex}`;

    if (!item || !item.name?.trim() || !item.role?.trim()) {
      toast.warning('Please enter Name and Role');
      return;
    }

    setSubmitting(prev => ({ ...prev, [itemKey]: true }));

    try {
      const savedToken = localStorage.getItem('ph_auth_token') || localStorage.getItem('auth_token') || sessionStorage.getItem('token');
      const headers = { 'Content-Type': 'application/json' };
      if (savedToken) {
        headers['Authorization'] = `Bearer ${savedToken}`;
      }

      const isNew = itemIndex >= originalData.items.length;
      const op = isNew ? 'add' : 'update';

      const response = await fetch(
        `${apiBase}/cms/field-change/submit`,
        {
          method: 'POST',
          headers,
          credentials: 'include',
          body: JSON.stringify({
            section_id: sectionId,
            field_id: `items.${itemIndex}.${op}`,
            field_name: `${isNew ? 'Add' : 'Update'} Leadership Profile - ${item.name}`,
            old_value: isNew ? null : (originalData.items[itemIndex] || {}),
            new_value: item,
            locale: 'en'
          })
        }
      );

      const result = await response.json();

      if (result.success || response.ok) {
        toast.success(`Profile for ${item.name} submitted for approval`);
        setPendingItems(prev => ({ ...prev, [itemIndex]: true }));
        setFormData(prev => ({
          ...prev,
          items: prev.items.map((c, i) => i === itemIndex ? item : c)
        }));
        setOriginalData(prev => {
          const nextItems = [...prev.items];
          nextItems[itemIndex] = item;
          return { ...prev, items: nextItems };
        });
        setEditingIndex(null);
        setModalData(null);
      } else {
        if (response.status === 409) {
          toast.error('This profile already has a pending approval');
        } else {
          toast.error(result.error || 'Submit failed');
        }
      }
    } catch (err) {
      toast.error('Error: ' + err.message);
    } finally {
      setSubmitting(prev => ({ ...prev, [itemKey]: false }));
    }
  };

  if (loading) {
    return (
      <div className="unified-loading-container">
        <div className="unified-loading-spinner" />
        <p className="unified-loading-text">Loading Leadership Team...</p>
      </div>
    );
  }

  return (
    <div className="unified-form-container">
      <ToastContainer />

      {/* Header */}
      <div className="unified-header">
        <div className="unified-header-left">
          <div className="unified-icon-badge">
            <Users size={22} />
          </div>
          <div>
            <h2 className="unified-title">Leadership Team & Academic Heads</h2>
            <p className="unified-subtitle">Manage executive directors, board trustees, and academic program leaders</p>
          </div>
        </div>
      </div>

      {/* Section Header Editors */}
      <div style={{ marginBottom: '24px', display: 'flex', flexDirection: 'column', gap: '14px' }}>
        {/* Section Tag / Kicker */}
        <div className="unified-field-card">
          <div className="unified-field-header">
            <label className="unified-label">Section Tag / Kicker</label>
            {pendingHeader.kicker && (
              <span className="unified-pending-pill"><Clock size={12} /> Pending Review</span>
            )}
          </div>
          <div className="unified-input-action-row">
            <input
              type="text"
              maxLength="60"
              className="unified-input"
              value={formData.kicker}
              onChange={(e) => handleFieldChange('kicker', e.target.value)}
              disabled={pendingHeader.kicker || submitting.kicker}
              placeholder="e.g. School Leadership"
            />
            <button
              type="button"
              className="unified-submit-button"
              onClick={() => handleSubmitHeaderField('kicker', 'Section Kicker')}
              disabled={submitting.kicker || pendingHeader.kicker || formData.kicker === originalData.kicker}
            >
              <Save size={14} /> {submitting.kicker ? 'Saving...' : 'Save'}
            </button>
          </div>
        </div>

        {/* Section Headline */}
        <div className="unified-field-card">
          <div className="unified-field-header">
            <label className="unified-label">Section Headline</label>
            {pendingHeader.title && (
              <span className="unified-pending-pill"><Clock size={12} /> Pending Review</span>
            )}
          </div>
          <div className="unified-input-action-row">
            <input
              type="text"
              maxLength="120"
              className="unified-input"
              value={formData.title}
              onChange={(e) => handleFieldChange('title', e.target.value)}
              disabled={pendingHeader.title || submitting.title}
              placeholder="e.g. Profiles of the Principal & Key Staff"
            />
            <button
              type="button"
              className="unified-submit-button"
              onClick={() => handleSubmitHeaderField('title', 'Section Headline')}
              disabled={submitting.title || pendingHeader.title || formData.title === originalData.title}
            >
              <Save size={14} /> {submitting.title ? 'Saving...' : 'Save'}
            </button>
          </div>
        </div>

        {/* Section Subtitle / Description */}
        <div className="unified-field-card">
          <div className="unified-field-header">
            <label className="unified-label">Section Subtitle / Description</label>
            {pendingHeader.subtitle && (
              <span className="unified-pending-pill"><Clock size={12} /> Pending Review</span>
            )}
          </div>
          <div className="unified-input-action-row" style={{ alignItems: 'flex-start' }}>
            <textarea
              maxLength="500"
              className="unified-textarea"
              rows="3"
              value={formData.subtitle}
              onChange={(e) => handleFieldChange('subtitle', e.target.value)}
              disabled={pendingHeader.subtitle || submitting.subtitle}
              placeholder="e.g. Meet the leaders who shape our culture, strengthen academics, and support every student journey."
            />
            <button
              type="button"
              className="unified-submit-button"
              style={{ marginTop: '2px' }}
              onClick={() => handleSubmitHeaderField('subtitle', 'Section Subtitle')}
              disabled={submitting.subtitle || pendingHeader.subtitle || formData.subtitle === originalData.subtitle}
            >
              <Save size={14} /> {submitting.subtitle ? 'Saving...' : 'Save'}
            </button>
          </div>
        </div>
      </div>

      {/* Action Row */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '20px' }}>
        <div>
          <h3 style={{ fontSize: '16px', fontWeight: '800', color: '#0f172a', margin: 0 }}>
            Team Members ({formData.items.length})
          </h3>
          <p style={{ fontSize: '13px', color: '#64748b', margin: '2px 0 0' }}>
            Executive leadership profiles featured on the public About Us page
          </p>
        </div>
        <button
          type="button"
          className="unified-submit-button"
          onClick={handleAddItem}
          style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}
        >
          <Plus size={16} /> Add Member
        </button>
      </div>

      {/* Members list (Vertical downward stack) */}
      {formData.items.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '40px 20px', background: '#f8fafc', borderRadius: '14px', border: '2px dashed #cbd5e1' }}>
          <Users size={38} color="#94a3b8" style={{ margin: '0 auto 12px' }} />
          <h4 style={{ fontSize: '15px', color: '#334155', margin: '0 0 4px', fontWeight: '700' }}>No Leadership Members Added</h4>
          <p style={{ fontSize: '13px', color: '#64748b', margin: 0 }}>Click "Add Member" above to create your first profile</p>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
          {formData.items.map((item, idx) => {
            const isPending = pendingItems[idx];
            const photoUrl = getMediaUrl(item.photo);

            return (
              <div
                key={idx}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '22px',
                  padding: '20px 24px',
                  borderRadius: '14px',
                  border: isPending ? '2px solid #f59e0b' : '1px solid #e2e8f0',
                  borderLeft: isPending ? '5px solid #f59e0b' : `5px solid ${idx % 2 === 0 ? '#2563eb' : '#6366f1'}`,
                  background: isPending ? '#fffdf7' : '#ffffff',
                  boxShadow: '0 2px 6px rgba(0,0,0,0.03)',
                  transition: 'all 0.2s ease'
                }}
              >
                {/* Photo Thumbnail */}
                <div
                  style={{
                    width: '90px',
                    height: '110px',
                    borderRadius: '10px',
                    overflow: 'hidden',
                    flexShrink: 0,
                    background: '#0f172a',
                    border: '1px solid #cbd5e1',
                    position: 'relative',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                >
                  {photoUrl ? (
                    <img
                      src={photoUrl}
                      alt={item.name}
                      style={{ width: '100%', height: '100%', objectFit: 'cover', cursor: 'pointer' }}
                      onClick={() => setLightboxImage({ src: photoUrl, title: item.name })}
                    />
                  ) : (
                    <div style={{ color: '#94a3b8', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '4px' }}>
                      <ImageIcon size={22} />
                      <span style={{ fontSize: '10px' }}>No photo</span>
                    </div>
                  )}
                  {photoUrl && (
                    <button
                      type="button"
                      onClick={() => setLightboxImage({ src: photoUrl, title: item.name })}
                      style={{
                        position: 'absolute',
                        bottom: '4px',
                        right: '4px',
                        background: 'rgba(0,0,0,0.65)',
                        color: '#fff',
                        border: 'none',
                        padding: '3px 5px',
                        borderRadius: '5px',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center'
                      }}
                      title="View Photo"
                    >
                      <Eye size={11} />
                    </button>
                  )}
                </div>

                {/* Info */}
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '4px', flexWrap: 'wrap' }}>
                    <h4 style={{ margin: 0, fontSize: '16px', fontWeight: '800', color: '#0f172a' }}>
                      {item.name || 'Unnamed Member'}
                    </h4>
                    {item.role && (
                      <span
                        style={{
                          padding: '2px 10px',
                          borderRadius: '6px',
                          background: '#eff6ff',
                          color: '#1d4ed8',
                          fontSize: '11.5px',
                          fontWeight: '700'
                        }}
                      >
                        {item.role}
                      </span>
                    )}
                    {isPending && (
                      <span className="unified-pending-pill" style={{ padding: '2px 8px', fontSize: '10px' }}>
                        <Clock size={10} /> Pending Review
                      </span>
                    )}
                  </div>
                  <p
                    style={{
                      margin: 0,
                      fontSize: '13px',
                      color: '#475569',
                      lineHeight: 1.5,
                      overflow: 'hidden',
                      display: '-webkit-box',
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: 'vertical'
                    }}
                  >
                    {item.bio || 'No biography entered yet.'}
                  </p>
                </div>

                {/* Actions */}
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexShrink: 0 }}>
                  <button
                    type="button"
                    className="unified-secondary-btn"
                    onClick={() => {
                      if (isPending) {
                        toast.info('This profile is awaiting approval');
                        return;
                      }
                      setEditingIndex(idx);
                      setModalData({ ...item });
                    }}
                    disabled={isPending}
                    style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}
                  >
                    <Edit3 size={14} /> Edit Profile
                  </button>
                  {!isPending && (
                    <button
                      type="button"
                      onClick={() => handleDeleteItem(idx)}
                      disabled={submitting[`del_${idx}`]}
                      style={{
                        padding: '8px 12px',
                        color: '#ef4444',
                        background: '#fef2f2',
                        border: '1px solid #fecaca',
                        borderRadius: '8px',
                        cursor: 'pointer',
                        display: 'inline-flex',
                        alignItems: 'center'
                      }}
                      title="Delete Profile"
                    >
                      <Trash2 size={14} />
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Edit/Add Member Modal */}
      {editingIndex !== null && modalData && (
        <div className="unified-modal-overlay" onClick={handleCloseModal}>
          <div className="unified-modal" onClick={(e) => e.stopPropagation()}>
            <div className="unified-modal-header">
              <div>
                <h3 style={{ margin: 0, fontSize: '17px', fontWeight: '800', color: '#0f172a' }}>
                  {modalData.name ? `Edit Profile: ${modalData.name}` : 'Add Leadership Member'}
                </h3>
                <p style={{ margin: '2px 0 0', fontSize: '13px', color: '#64748b' }}>
                  Manage member name, title/role, portrait photo, and biography
                </p>
              </div>
              <button
                type="button"
                onClick={handleCloseModal}
                style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#64748b' }}
              >
                <X size={20} />
              </button>
            </div>

            <div className="unified-modal-body">
              {/* Photo Preview and Upload */}
              <div style={{ display: 'flex', alignItems: 'center', gap: '20px', marginBottom: '20px', padding: '16px', background: '#f8fafc', borderRadius: '12px', border: '1px solid #e2e8f0' }}>
                <div style={{ width: '90px', height: '110px', borderRadius: '8px', overflow: 'hidden', background: '#0f172a', border: '1px solid #cbd5e1', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  {modalData.photo && getMediaUrl(modalData.photo) ? (
                    <img
                      src={getMediaUrl(modalData.photo)}
                      alt="Preview"
                      style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                    />
                  ) : (
                    <ImageIcon size={28} color="#94a3b8" />
                  )}
                </div>
                <div style={{ flex: 1 }}>
                  <label className="unified-label" style={{ marginBottom: '6px' }}>Member Portrait</label>
                  <p style={{ margin: '0 0 10px', fontSize: '12px', color: '#64748b' }}>
                    JPG, PNG, WebP • Max 5MB • Recommended: 400×500px (portrait)
                  </p>
                  <div style={{ position: 'relative', display: 'inline-block' }}>
                    <input
                      type="file"
                      id="leadership-photo-file"
                      accept="image/jpeg,image/png,image/webp"
                      onChange={handleImageUpload}
                      disabled={imageUploading}
                      style={{ position: 'absolute', inset: 0, opacity: 0, cursor: 'pointer' }}
                    />
                    <label htmlFor="leadership-photo-file" className="unified-secondary-btn" style={{ cursor: 'pointer' }}>
                      <Upload size={14} /> {imageUploading ? `Uploading ${imageProgress}%...` : 'Browse & Upload Photo'}
                    </label>
                  </div>
                </div>
              </div>

              {/* Name */}
              <div style={{ marginBottom: '16px' }}>
                <label className="unified-label" style={{ marginBottom: '8px' }}>Full Name & Honorifics</label>
                <input
                  type="text"
                  className="unified-input"
                  value={modalData.name || ''}
                  onChange={(e) => handleModalDataChange('name', e.target.value)}
                  placeholder="e.g. Dr. Maria Elizabeth, M.Ed."
                />
              </div>

              {/* Role */}
              <div style={{ marginBottom: '16px' }}>
                <label className="unified-label" style={{ marginBottom: '8px' }}>Official Title / Position</label>
                <input
                  type="text"
                  className="unified-input"
                  value={modalData.role || ''}
                  onChange={(e) => handleModalDataChange('role', e.target.value)}
                  placeholder="e.g. Head of Primary Education"
                />
              </div>

              {/* Bio */}
              <div style={{ marginBottom: '10px' }}>
                <label className="unified-label" style={{ marginBottom: '8px' }}>Professional Biography</label>
                <textarea
                  className="unified-textarea"
                  rows="4"
                  value={modalData.bio || ''}
                  onChange={(e) => handleModalDataChange('bio', e.target.value)}
                  placeholder="Academic background, leadership achievements, and philosophy..."
                />
              </div>
            </div>

            <div className="unified-modal-footer">
              <button
                type="button"
                className="unified-secondary-btn"
                onClick={handleCloseModal}
              >
                Cancel
              </button>
              <button
                type="button"
                className="unified-submit-button"
                onClick={handleSubmitModal}
                disabled={submitting[`item_${editingIndex}`] || !modalData.name?.trim() || !modalData.role?.trim() || imageUploading}
              >
                <Save size={14} /> {submitting[`item_${editingIndex}`] ? 'Submitting...' : 'Save & Submit Approval'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Lightbox Modal */}
      {lightboxImage && (
        <div className="unified-modal-overlay" onClick={() => setLightboxImage(null)}>
          <div style={{ maxWidth: '90vw', maxHeight: '90vh', position: 'relative' }} onClick={(e) => e.stopPropagation()}>
            <button
              type="button"
              onClick={() => setLightboxImage(null)}
              style={{
                position: 'absolute',
                top: '-14px',
                right: '-14px',
                background: '#ffffff',
                border: 'none',
                borderRadius: '50%',
                width: '32px',
                height: '32px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                boxShadow: '0 4px 12px rgba(0,0,0,0.2)',
                cursor: 'pointer'
              }}
            >
              <X size={18} color="#0f172a" />
            </button>
            <img
              src={lightboxImage.src}
              alt={lightboxImage.title || 'Leadership photo'}
              style={{ maxWidth: '100%', maxHeight: '85vh', borderRadius: '12px', boxShadow: '0 20px 40px rgba(0,0,0,0.5)' }}
            />
          </div>
        </div>
      )}
    </div>
  );
}
